import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { AREA_TO_REGION, REGIONS } from './data/regions.ts'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { GlobalSearch } from './components/global-search.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import {
	ThemeSwitch,
	useOptionalTheme,
	useTheme,
} from './routes/resources/theme-switch.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { type Theme, getTheme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser, userHasRole } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => [
	{ title: data ? 'Good to Order' : 'Error | Good to Order' },
	{
		name: 'description',
		content: 'Find good food nearby in London and know what to order first.',
	},
]

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							email: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()
	const publishedAreaTags = await prisma.tag.findMany({
		where: {
			citySlug: 'london',
			type: 'area',
			primaryAreaRestaurants: {
				some: { status: 'published' },
			},
		},
		select: {
			id: true,
			name: true,
			slug: true,
			_count: {
				select: {
					primaryAreaRestaurants: {
						where: { status: 'published' },
					},
				},
			},
		},
		orderBy: { name: 'asc' },
	})

	const groupedAreas = REGIONS.map((region) => ({
		name: region.name,
		slug: region.slug,
		areas: publishedAreaTags
			.filter((area) => AREA_TO_REGION[area.slug] === region.slug)
			.map((area) => ({
				id: area.id,
				name: area.name,
				slug: area.slug,
				restaurantCount: area._count.primaryAreaRestaurants,
			})),
	})).filter((group) => group.areas.length > 0)

	const uncategorizedAreas = publishedAreaTags
		.filter((area) => !AREA_TO_REGION[area.slug])
		.map((area) => ({
			id: area.id,
			name: area.name,
			slug: area.slug,
			restaurantCount: area._count.primaryAreaRestaurants,
		}))

	const areaNavGroups = uncategorizedAreas.length
		? [
				...groupedAreas,
				{ name: 'Other', slug: 'other', areas: uncategorizedAreas },
			]
		: groupedAreas

	return data(
		{
			user,
			areaNavGroups,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	theme = 'dark',
	env = {},
	origin = 'https://goodtoorder.com',
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string | undefined>
	origin?: string
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'

	// Organization schema for Google to identify logo
	// Google prefers PNG for logo (112x112 min, 512x512 recommended)
	const organizationSchema = {
		'@context': 'https://schema.org',
		'@type': 'Organization',
		name: 'Good to Order',
		url: origin,
		logo: {
			'@type': 'ImageObject',
			url: `${origin}/favicons/android-chrome-512x512.png`,
			width: 512,
			height: 512,
		},
		sameAs: [],
	}

	// WebSite schema for sitelinks search box
	const webSiteSchema = {
		'@context': 'https://schema.org',
		'@type': 'WebSite',
		name: 'Good to Order',
		url: origin,
		description: 'Find somewhere good nearby. Know what to order.',
	}

	return (
		<html
			lang="en"
			data-theme="goodtoorder"
			className={`${theme} h-full overflow-x-hidden`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
				{/* Organization & WebSite schema for Google */}
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify(organizationSchema),
					}}
				/>
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
				/>
				{/* Google Analytics */}
				<script
					async
					src="https://www.googletagmanager.com/gtag/js?id=G-2Z2NR0T4T2"
				/>
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `
							window.dataLayer = window.dataLayer || [];
							function gtag(){dataLayer.push(arguments);}
							gtag('js', new Date());
							gtag('config', 'G-2Z2NR0T4T2');
						`,
					}}
				/>
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	const theme = useOptionalTheme()
	return (
		<Document
			nonce={nonce}
			theme={theme}
			env={data?.ENV}
			origin={data?.requestInfo?.origin}
		>
			{children}
		</Document>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const theme = useTheme()
	const isAdmin =
		userHasRole(user ?? null, 'admin') || user?.email === 'marcusps1@gmail.com'
	useToast(data.toast)

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="min-h-screen bg-[#05050a] text-[#f8f8ff]">
				<header className="navbar sticky top-0 z-30 border-b border-white/10 bg-[#05050a]/90 backdrop-blur">
					<div className="container mx-auto flex flex-wrap items-center gap-4 px-4 py-3">
						<div className="flex flex-1 items-center gap-3">
							<Logo />
							<div className="hidden md:block">
								<p className="text-sm font-semibold text-[#f8f8ff]">
									Good to Order
								</p>
									<p className="text-xs text-[#b0b0e0]">
										Good food nearby. Know what to order.
									</p>
							</div>
						</div>
						<nav className="hidden items-center gap-6 text-sm font-semibold text-[#d6dcff] md:flex">
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<button className="inline-flex items-center gap-2 hover:text-[#00e0ff]">
										<span>Areas</span>
										<span className="text-xs text-[#7070a0]">▼</span>
									</button>
								</DropdownMenuTrigger>
								<DropdownMenuContent
									align="start"
									className="max-h-[70vh] w-72 overflow-y-auto"
								>
									<DropdownMenuLabel>
										All London areas with restaurants
									</DropdownMenuLabel>
									<DropdownMenuSeparator />
									{data.areaNavGroups.map((group) => (
										<DropdownMenuSub key={group.slug}>
											<DropdownMenuSubTrigger>
												{group.name}
											</DropdownMenuSubTrigger>
											<DropdownMenuSubContent className="max-h-[70vh] w-64 overflow-y-auto">
												<DropdownMenuLabel>{group.name}</DropdownMenuLabel>
												<DropdownMenuSeparator />
												<div className="grid gap-1 p-1">
													{group.areas.map((area) => (
														<Link
															key={area.id}
															to={`/london/best/restaurants-${area.slug}`}
															className="flex items-center justify-between rounded-sm px-2 py-1.5 text-sm transition-colors hover:bg-[#00e0ff] hover:text-[#05050a]"
														>
															<span>{area.name}</span>
															<span className="text-xs text-[#7070a0]">
																{area.restaurantCount}
															</span>
														</Link>
													))}
												</div>
											</DropdownMenuSubContent>
										</DropdownMenuSub>
									))}
									<DropdownMenuSeparator />
									<Link
										className="block rounded-sm px-2 py-1.5 text-sm hover:bg-[#00e0ff] hover:text-[#05050a]"
										to="/london"
									>
										View all areas
									</Link>
								</DropdownMenuContent>
							</DropdownMenu>

							{isAdmin && (
								<>
									<span className="text-[#4b4b68]">|</span>
									<Link
										className="hover:text-[#00e0ff]"
										to="/admin/restaurants"
									>
										Restaurants
									</Link>
									<Link
										className="hover:text-[#00e0ff]"
										to="/admin/collections"
									>
										Collections
									</Link>
									<Link className="hover:text-[#00e0ff]" to="/admin/import">
										Import
									</Link>
									<Link className="hover:text-[#00e0ff]" to="/admin/feedback">
										Feedback
									</Link>
								</>
							)}
						</nav>
						<div className="flex items-center gap-3">
							{user ? (
								<UserDropdown />
							) : (
								<Link className="btn btn-primary btn-sm" to="/login">
									Log in
								</Link>
							)}
						</div>
					</div>
				</header>

				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>

				<footer className="border-t border-white/10 bg-[#0b0b12]">
					<div className="container mx-auto flex flex-col gap-4 px-4 py-6 text-sm text-[#b0b0e0] md:flex-row md:items-center md:justify-between">
						<div className="flex flex-wrap items-center gap-4">
							<div className="flex items-center gap-2">
								<Logo />
								<span>Good to Order</span>
							</div>
							<nav className="flex flex-wrap items-center gap-4 text-sm">
								<Link className="hover:text-[#00e0ff]" to="/about">
									About
								</Link>
								<Link className="hover:text-[#00e0ff]" to="/privacy">
									Privacy
								</Link>
								<Link className="hover:text-[#00e0ff]" to="/support">
									Support
								</Link>
							</nav>
						</div>
						<ThemeSwitch userPreference={data.requestInfo.userPrefs.theme} />
					</div>
				</footer>
			</div>
			<GlobalSearch />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function Logo() {
	return (
		<Link
			to="/"
			className="flex items-center gap-3 transition hover:opacity-90"
		>
			<img
				src="/images/logo-gto.svg"
				alt="Good to Order logo"
				className="h-10 w-auto rounded-md shadow-sm"
			/>
		</Link>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
